import { isUndefined } from 'lodash4';
import * as moment from 'moment';
import { ConditionalStrategySafe, StrategySafe } from 'utility-ts-strategy';

export interface IAPTermStatus {
  tooltip_content: string;
  tooltip_title: string;
  status_title: string;
  status_title_plain: string;
  button_title: string;
  show_tooltip: boolean;
  button_label: string;
}

export type ap_term_status_type =
  | 'DEFAULT'
  | 'BEFORE_REG_OPEN'
  | 'REG_OPEN'
  | 'PRIORITY_BEFORE_REG_OPEN'
  | 'PRIORITY_REG_OPEN'
  | 'BEFORE_TERM_START'
  | 'TERM_IN_PROGRESS'
  | 'TERM_COMPLETED';

export const APTermStatusStrategy = new StrategySafe<IAPTermStatus, ap_term_status_type>({
  DEFAULT: {
    status_title: `<unset>`,
    status_title_plain: `<unset>`,
    tooltip_title: `<unset>`,
    tooltip_content: `<unset>`,
    show_tooltip: true,
    button_title: `<unset>`,
    button_label: `<unset>`
  },
  BEFORE_REG_OPEN: {
    status_title: `Term Status: Term begins {{term_start_date}}`,
    status_title_plain: 'Term begins ${ term_start_date }',
    tooltip_title: `Registration`,
    show_tooltip: false,
    tooltip_content: `<strong class="text-bold">{{registration_time_until}} until registration is open</strong><p class="text maz">Registration begins {{registration_start_date_year}}</p>`,
    button_title: `Times not available yet`,
    button_label: `Pick Times`
  },
  REG_OPEN: {
    status_title: `Term Status: Registration Open`,
    status_title_plain: `Term Status: Registration Open`,
    tooltip_title: `Registration`,
    show_tooltip: false,
    tooltip_content: `<strong class="text-bold">Registration open</strong><p class="text maz">Registration begins {{registration_start_date_year}}</p>`,
    button_title: `View or edit your schedule`,
    button_label: `View / Edit Schedule`
  },
  PRIORITY_BEFORE_REG_OPEN: {
    status_title: `<div l-kind l-align='center' l-justify='left' l-width-fixed ><hi-icon kind="rocket_filled" theme="gray" aria-hidden="true"></hi-icon><span class="clr-gray_90-txt text-bold">Priority Registration</span></div>`,
    status_title_plain: `Priority Registration`,
    tooltip_title: `Priority Registration`,
    show_tooltip: true,
    tooltip_content: `<strong class="text-bold">You get to register before other students!</strong><p class="text maz">Registration begins {{priority_registration_start_date_year}}</p>`,
    button_title: `Times available - Build your schedule`,
    button_label: `Pick Times`
  },
  PRIORITY_REG_OPEN: {
    status_title: `<div l-kind l-align='center' l-justify='left' l-width-fixed ><hi-icon kind="rocket_filled" theme="gray" aria-hidden="true"></hi-icon><span class="clr-gray_90-txt text-bold">Priority Registration</span></div>`,
    status_title_plain: `Priority Registration Open`,
    tooltip_title: `Priority Registration`,
    show_tooltip: true,
    tooltip_content: `<strong class="text-bold">Priority registration open!</strong><p class="text maz">Registration begins {{priority_registration_start_date_year}}</p>`,
    button_title: `View or edit your schedule`,
    button_label: `View / Edit Schedule`
  },
  BEFORE_TERM_START: {
    status_title: `Term begins {{term_start_date}}`,
    status_title_plain: 'Term begins ${ term_start_date }',
    tooltip_title: `Term begins soon`,
    show_tooltip: true,
    tooltip_content: `<strong class="text-bold">{{term_time_until}} until your term starts</strong><p class="text maz">{{term_start_date_year}} - {{term_end_date_year}}</p>`,
    button_title: `View or edit your schedule`,
    button_label: `View / Edit Schedule`
  },
  TERM_IN_PROGRESS: {
    status_title: `Term Status: In Progress`,
    status_title_plain: `In Progress`,
    tooltip_title: `In Progress`,
    show_tooltip: false,
    tooltip_content: `<strong class="text-bold">{{term_time_left}} left in this term</strong><p class="text maz">{{term_start_date_year}} - {{term_end_date_year}}</p>`,
    button_title: `View or edit your schedule`,
    button_label: `View / Edit Schedule`
  },
  TERM_COMPLETED: {
    status_title: `Term Status: Completed`,
    status_title_plain: `Completed`,
    tooltip_title: `Completed`,
    show_tooltip: true,
    tooltip_content: `<p class="text maz">{{term_start_date_year}} - {{term_end_date_year}}</p>`,
    button_title: `<unset>`,
    button_label: `<unset>`
  }
});

// TODO make these moment objects
interface ITermStatusConditionContext {
  registration_start_dt: moment.Moment;
  registration_end_dt: moment.Moment;
  term_start_dt: moment.Moment;
  term_end_dt: moment.Moment;
  priority_registration_start_dt?: moment.Moment;
  priority_registration_end_dt?: moment.Moment;
}

const isDateNotDefaulted = (date: moment.Moment): boolean => date.year() > 1900;

export const APTermStatusConditionalStrategy = new ConditionalStrategySafe<
  ITermStatusConditionContext,
  ap_term_status_type
>({
  PRIORITY_BEFORE_REG_OPEN: (context: ITermStatusConditionContext) =>
    !isUndefined(context.priority_registration_start_dt) &&
    moment().isBefore(context.priority_registration_start_dt as moment.Moment),
  PRIORITY_REG_OPEN: (context: ITermStatusConditionContext) =>
    !!context.priority_registration_start_dt &&
    context.priority_registration_start_dt.isSameOrBefore(moment()) &&
    !!context.priority_registration_end_dt &&
    context.priority_registration_end_dt.isSameOrAfter(moment()),
  BEFORE_REG_OPEN: (context: ITermStatusConditionContext) =>
    isDateNotDefaulted(context.registration_start_dt) && moment().isBefore(context.registration_start_dt),
  REG_OPEN: (context: ITermStatusConditionContext) =>
    isDateNotDefaulted(context.registration_start_dt) &&
    context.registration_start_dt.isSameOrBefore(moment()) &&
    context.registration_end_dt.isSameOrAfter(moment()),
  BEFORE_TERM_START: (context: ITermStatusConditionContext) =>
    context.registration_end_dt.isBefore(moment()) && context.term_start_dt.isAfter(moment()),
  TERM_IN_PROGRESS: (context: ITermStatusConditionContext) =>
    context.term_start_dt.isSameOrBefore(moment()) && context.term_end_dt.isSameOrAfter(moment()),
  TERM_COMPLETED: (context: ITermStatusConditionContext) => context.term_end_dt.isBefore(moment()),
  DEFAULT: (context: ITermStatusConditionContext) => false
});



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/global/strategies/ap_term_status_strategy.ts